// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ProductQtyOrderedStyles } from "@bg/Overrides/Components/ProductQuantityOrdered";
import { cartLineCardExpandedStyles } from "@bg/Overrides/Widgets/Cart/CartLineCardExpanded";
import { cartLinesStyles, LinesHeader } from "@bg/Overrides/Widgets/Cart/CartLines";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import calculateTotal from "@insite/client-framework/Store/Pages/QuickOrder/Handlers/CalculateTotal";
import changeProductQty from "@insite/client-framework/Store/Pages/QuickOrder/Handlers/ChangeProductQty";
import removeProduct from "@insite/client-framework/Store/Pages/QuickOrder/Handlers/RemoveProduct";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import { ProductAddToListLinkStyles } from "@insite/content-library/Components/ProductAddToListLink";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import { TextFieldPresentationProps, TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import debounce from "lodash/debounce";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    productInfos: state.pages.quickOrder.productInfos,
    products: state.pages.quickOrder.productInfos.map(o => getProductState(state, o.productId).value) as (
        | ProductModel
        | undefined
    )[],
    total: state.pages.quickOrder.total,
    currencySymbol: state.context.session.currency?.currencySymbol || "",
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
});

const mapDispatchToProps = {
    calculateTotal,
    changeProductQty,
    removeProduct,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface QuickOrderProductListStyles {
    wrapper?: InjectableCss;
    cardContainer?: CardContainerStyles;
    gridContainer?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    productInfoGridItem?: GridItemProps;
    productInfoContainer?: GridContainerProps;
    brandAndProductDescriptionGridItem?: GridItemProps;
    brand?: ProductBrandStyles;
    productDescription?: ProductDescriptionStyles;
    productNumberText?: TypographyPresentationProps;
    availability?: ProductAvailabilityStyles;
    priceAndQuantityGridItem?: GridItemProps;
    priceAndQuantityContainer?: GridContainerProps;
    priceGridItem?: GridItemProps;
    price?: ProductPriceStyles;
    quantityGridItem?: GridItemProps;
    quantityOrdered?: TextFieldProps;
    extendedPriceGridItem?: GridItemProps;
    extendedPriceText?: TypographyPresentationProps;
    removeProductGridItem?: GridItemProps;
    removeProductClickable?: ClickableProps;
    removeProductIcon?: IconPresentationProps;
    addToListLink?: ProductAddToListLinkStyles;
    totalText?: TypographyPresentationProps;
    productQty?: ProductQtyOrderedStyles;
}

export const quickOrderProductListStyles: QuickOrderProductListStyles = {
    wrapper: {
        css: css`
            padding-bottom: 20px;
            margin-top: 20px;
        `,
    },
    cardContainer: {
        cardDivider: {
            css: css`
                margin: 0 20px;
                width: 100%;
                border-top: 1px solid ${getColor("common.border")};
                &:last-child {
                    border-bottom: 1px solid ${getColor("common.border")};
                }
            `,
        },
    },
    productImageGridItem: {
        width: 2,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-left: 0;
                        padding-right: 0;
                        font-size: ${fontConfig.fontSize.legend}px;
                    `,
                    css`
                        padding-left: 0;
                        padding-right: 0;
                        font-size: ${fontConfig.fontSize.legend}px;
                    `,
                    css`
                        padding-left: 0;
                    `,
                    css`
                        padding-left: 0;
                    `,
                    css`
                        padding-left: 0;
                    `,
                ])}
        `,
    },
    productInfoGridItem: { width: [7, 7, 7, 8, 8] },
    brandAndProductDescriptionGridItem: {
        width: [12, 12, 12, 6, 5],
        css: css`
            flex-direction: column;
        `,
    },
    availability: {
        container: {
            css: css`
                margin-top: 10px;
            `,
        },
    },
    priceAndQuantityGridItem: { width: [12, 12, 12, 6, 7] },
    priceGridItem: {
        width: [12, 12, 4, 4, 4],
        css: css`
            flex-direction: column;
            justify-content: center;
        `,
    },
    quantityGridItem: { width: [6, 6, 5, 5, 4] },
    quantityOrdered: {
        labelPosition: "left",
        labelProps: {
            css: css`
                width: auto;
                display: inline-flex;
            `,
        },
        cssOverrides: {
            formInputWrapper: css`
                width: 70px;
            `,
        },
    },
    extendedPriceGridItem: { width: [6, 6, 3, 3, 4] },
    extendedPriceText: {
        css: css`
            font-weight: ${fontConfig.weight.bold};
            align-self: center;
        `,
    },
    removeProductGridItem: {
        css: css`
            text-align: center;
            display: block;
        `,
        width: [3, 3, 3, 2, 2],
    },
    removeProductClickable: {
        css: css`
            margin-left: auto;
            margin-right: auto;
            margin-bottom: 15px;
        `,
    },
    removeProductIcon: { src: "XCircle" },
    totalText: {
        css: css`
            float: right;
            font-weight: ${fontConfig.weight.bold};
            margin: 15px 0;
        `,
    },
    productQty: {
        inputStyles: {
            cssOverrides: {
                formField: css`
                    height: 40px;
                `,
                formInputWrapper: css`
                    &&&&&&& {
                        margin-top: 0;
                    }
                `,
            },
        },
    },
};

const styles = mergeToNew(quickOrderProductListStyles, cartLineCardExpandedStyles);
const StyledSection = getStyledWrapper("section");

const QuickOrderProductList = ({
    products,
    productInfos,
    total,
    currencySymbol,
    calculateTotal,
    changeProductQty,
    removeProduct,
    vatPriceDisplay,
    enableVat,
}: Props) => {
    if (!products || products.length === 0) {
        return null;
    }

    React.useEffect(() => calculateTotal(), [products]);

    const removeProductHandler = (productInfo: ProductInfo) => {
        removeProduct({ productId: productInfo.productId, unitOfMeasure: productInfo.unitOfMeasure });
    };

    const productListDisplay = products.map((product, index) => {
        if (!product || index >= productInfos.length) {
            return;
        }
        const productInfo = productInfos[index];

        const productContext: ProductContextModel = {
            product,
            productInfo,
            onQtyOrderedChanged: debounce(qtyOrdered => {
                changeProductQty({
                    qtyOrdered,
                    productId: product.id,
                    unitOfMeasure: productInfo.unitOfMeasure,
                    product,
                });
            }, 300),
        };

        // const showColumnHeader = !index || windowSize <= 1650;
        const description = productContext.product.productTitle;
        const productDetailPath = productContext.productInfo.productDetailPath;
        return (
            <ProductContext.Provider value={productContext} key={product.id + productInfo.unitOfMeasure}>
                <StyledWrapper
                    {...styles.container}
                    data-test-selector={`cartline_expanded_${productInfo.productId}_${productInfo.unitOfMeasure}`}
                >
                    <StyledWrapper {...styles.rightSection}>
                        <StyledWrapper {...styles.imageWrapper}>
                            <ProductImage product={productContext} extendedStyles={styles.productImage} />
                        </StyledWrapper>

                        <StyledWrapper {...styles.descAndNumbersWrapper}>
                            <StyledWrapper {...styles.productBrandAndDescriptionGridItem}>
                                {product.brand && (
                                    <ProductBrand brand={product.brand} extendedStyles={styles.productBrand} />
                                )}
                                <ProductDescription
                                    product={productContext}
                                    extendedStyles={styles.productDescription}
                                />
                            </StyledWrapper>
                            <StyledWrapper {...styles.productPartNumbersWrapper}>
                                <ProductPartNumbers
                                    productNumber={product.detail?.sku || product.productNumber}
                                    customerProductNumber={product.customerProductNumber}
                                    manufacturerItem={product.manufacturerItem}
                                    extendedStyles={styles.productPartNumbers}
                                />
                            </StyledWrapper>
                            {!product.quoteRequired && (
                                <ProductContextAvailability extendedStyles={styles.productAvailability} />
                            )}
                        </StyledWrapper>
                    </StyledWrapper>

                    <StyledWrapper {...styles.leftSection}>
                        <StyledWrapper {...styles.priceQtywrapper}>
                            {!productInfo.pricing?.additionalResults?.hasOwnProperty("noPriceFound") ||
                            (productInfo.pricing?.additionalResults?.hasOwnProperty("noPriceFound") &&
                                productInfo?.pricing?.additionalResults?.["noPriceFound"].toLowerCase() !== "true") ? (
                                <ProductPrice
                                    product={productContext}
                                    currencySymbol={currencySymbol}
                                    showSavings={false}
                                    extendedStyles={styles.productPrice}
                                />
                            ) : (
                                <Typography
                                    variant="h6"
                                    forwardAs="span"
                                    weight={fontConfig.weight.bold}
                                    css={css`
                                        margin-bottom: 0;
                                        ${styles.productPrice?.wrapper?.css}
                                    `}
                                >
                                    <legend>{translate("Price/Unit")}</legend>
                                    N/A
                                </Typography>
                            )}

                            <StyledWrapper {...styles.quantityGridItem}>
                                <ProductQuantityOrdered
                                    labelOverride={<Hidden hidden={true}>{translate("Quantity")}</Hidden>}
                                    extendedStyles={quickOrderProductListStyles.productQty as any}
                                />
                            </StyledWrapper>
                            {!product.quoteRequired && !productInfo.pricing?.requiresRealTimePrice && (
                                <StyledWrapper {...styles.extendedUnitNetPriceGridItem}>
                                    <Typography as="legend">{translate("Total")}</Typography>
                                    {!productInfo.pricing?.additionalResults?.hasOwnProperty("noPriceFound") ||
                                    (productInfo.pricing?.additionalResults?.hasOwnProperty("noPriceFound") &&
                                        productInfo?.pricing?.additionalResults?.["noPriceFound"].toLowerCase() !==
                                            "true") ? (
                                        <Typography
                                            {...styles.extendedUnitNetPriceText}
                                            data-test-selector="quickOrder_ProductPrice"
                                        >
                                            {enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                                ? productInfo.pricing?.extendedUnitRegularPriceWithVatDisplay
                                                : productInfo.pricing?.extendedUnitNetPriceDisplay}
                                        </Typography>
                                    ) : (
                                        <Typography {...styles.extendedUnitNetPriceText}>N/A</Typography>
                                    )}

                                    {enableVat && (
                                        <>
                                            <Typography as="p" {...styles.vatLabelText}>
                                                {vatPriceDisplay === "DisplayWithVat" ||
                                                vatPriceDisplay === "DisplayWithAndWithoutVat"
                                                    ? `${translate("Inc. VAT")} (${productInfo.pricing?.vatRate}%)`
                                                    : translate("Ex. VAT")}
                                            </Typography>
                                            {vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                                <>
                                                    <Typography {...styles.subtotalWithoutVatText}>
                                                        {productInfo.pricing?.extendedUnitNetPriceDisplay}
                                                    </Typography>
                                                    <Typography as="p" {...styles.vatLabelText}>
                                                        {translate("Ex. VAT")}
                                                    </Typography>
                                                </>
                                            )}
                                        </>
                                    )}
                                </StyledWrapper>
                            )}
                        </StyledWrapper>
                        {productInfo.pricing?.additionalResults?.hasOwnProperty("noPriceFound") &&
                            productInfo.pricing?.additionalResults?.["noPriceFound"].toLowerCase() === "true" && (
                                <StyledWrapper>
                                    <Typography
                                        color="danger"
                                        size={fontConfig.fontSize.xSmall}
                                        lineHeight={fontConfig.lineHeight.singleLineSmall}
                                    >
                                        {siteMessage("Custom_Price_FailedToLoad")}
                                    </Typography>
                                </StyledWrapper>
                            )}
                    </StyledWrapper>
                    <StyledWrapper {...styles.removeCartLineGridItem}>
                        <Clickable
                            onClick={() => removeProductHandler(productInfo)}
                            data-test-selector="quickOrder_RemoveProduct"
                        >
                            <VisuallyHidden>{translate("Remove item")}</VisuallyHidden>
                            <IconMemo {...cartLineCardExpandedStyles.removeCartLineIcon} />
                        </Clickable>
                    </StyledWrapper>
                </StyledWrapper>
            </ProductContext.Provider>
        );
    });

    return (
        <>
            <StyledSection {...cartLinesStyles.wrapper}>
                <LinesHeader editable />
                {productListDisplay}
            </StyledSection>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(QuickOrderProductList),
    definition: {
        group: "Quick Order",
        icon: "List",
        allowedContexts: ["QuickOrderPage"],
    },
};

export default widgetModule;
